import { useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface Location {
  locationId: string;
  name: string;
}

interface LocationBadgesProps {
  locations: Location[];
  isLoading?: boolean;
  className?: string;
}

const MAX_VISIBLE_BADGES = 2;

export function LocationBadges({ locations, isLoading, className }: LocationBadgesProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Loading state
  if (isLoading || locations.length === 0) {
    return (
      <div className={className}>
        <Skeleton className="h-8 w-40" />
      </div>
    );
  }

  // Always show inline for ≤2 locations (including single location)
  if (locations.length <= MAX_VISIBLE_BADGES) {
    return (
      <ul className={cn("list-disc list-outside pl-4 text-sm", className)}>
        {locations.map(location => (
          <li key={location.locationId}>
            {location.name}
          </li>
        ))}
      </ul>
    );
  }

  // Collapsible for 3+ locations
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={className}>
      <CollapsibleTrigger asChild>
        <Button 
          variant="text" 
          size="sm"
          className="h-8 px-2 text-sm"
        >
          {t("locations.locationsSelected", { count: locations.length })}
          {isOpen ? (
            <ChevronUp className="ml-1 h-4 w-4" />
          ) : (
            <ChevronDown className="ml-1 h-4 w-4" />
          )}
        </Button>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        <ul className="list-disc list-outside pl-4 text-sm">
          {locations.map(location => (
            <li key={location.locationId}>
              {location.name}
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

